import { useState, useEffect } from "react";
import { requestPermissionAndToken, onForegroundMessage } from "../firebase";
import { api } from "../services/api";
import Card from "./ui/Card";
import Button from "./ui/Button";
import Badge from "./ui/Badge";

export default function NotificationSettings() {
  const [permission, setPermission] = useState(Notification.permission);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    // Check current permission status
    setPermission(Notification.permission);
    
    // Check if we already have a token stored
    if (localStorage.getItem("push_registered_v1") === "1") {
      setToken("registered");
    }
    
    // Listen for foreground messages
    onForegroundMessage((payload) => {
      console.log("Foreground notification received:", payload);
      if (Notification.permission === "granted") {
        new Notification(payload.notification.title, {
          body: payload.notification.body,
          icon: "/favicon.ico"
        });
      }
    });
  }, []);

  const requestNotificationPermission = async () => {
    setLoading(true);
    setMessage("");

    try {
      if (!("Notification" in window)) {
        setMessage("This browser does not support notifications");
        setLoading(false);
        return;
      }

      let permission = Notification.permission;
      
      if (permission === "default") {
        permission = await Notification.requestPermission();
      }

      setPermission(permission);

      if (permission === "granted") {
        // Get FCM token
        const fcmToken = await requestPermissionAndToken();
        
        if (fcmToken) {
          // Save token to backend
          await api("/save-token", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token: fcmToken }),
          });
          
          setToken(fcmToken);
          setMessage("✅ Notifications enabled successfully!");
          localStorage.setItem("push_registered_v1", "1");
        } else {
          setMessage("❌ Failed to get notification token");
        }
      } else if (permission === "denied") {
        setMessage("❌ Notifications blocked. Please enable them in your browser settings.");
      }
    } catch (error) {
      console.error("Notification setup error:", error);
      setMessage("❌ Failed to setup notifications: " + error.message);
    } finally {
      setLoading(false);
    }
  };



  const getPermissionStatus = () => {
    switch (permission) {
      case "granted":
        return { variant: "success", text: "Enabled" };
      case "denied":
        return { variant: "danger", text: "Blocked" };
      default:
        return { variant: "warning", text: "Not Set" };
    }
  };

  const status = getPermissionStatus();

  return (
    <Card>
      <Card.Header>
        <Card.Title>Notification Settings</Card.Title>
        <Card.Description>
          Configure how you receive website downtime alerts
        </Card.Description>
      </Card.Header>

      <Card.Content className="space-y-6">
        {/* Browser Notifications */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100">Browser Notifications</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Receive popup notifications in your browser
              </p>
            </div>
            <Badge variant={status.variant}>{status.text}</Badge>
          </div>

          {permission !== "granted" && (
            <Button
              variant="primary"
              size="sm"
              onClick={requestNotificationPermission}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Setting up...
                </div>
              ) : (
                "Enable Browser Notifications"
              )}
            </Button>
          )}
        </div>

        {/* Email Notifications */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100">Email Notifications</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Always enabled for your account email and any additional emails you configure
              </p>
            </div>
            <Badge variant="success">Always Enabled</Badge>
          </div>
        </div>

        {/* Mobile Notifications */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100">Mobile Notifications</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Push notifications on iOS and Android devices
              </p>
            </div>
            <Badge variant={token ? "success" : "warning"}>
              {token ? "Configured" : "Not Configured"}
            </Badge>
          </div>
          
          {!token && (
            <div className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-md">
              Mobile push notifications require the SiteScope mobile app to be installed
            </div>
          )}
        </div>

        {message && (
          <div className={`rounded-md p-3 ${
            message.includes("✅") ? 'bg-green-50' : 
            message.includes("❌") ? 'bg-red-50' : 'bg-blue-50'
          }`}>
            <div className={`text-sm ${
              message.includes("✅") ? 'text-green-700' : 
              message.includes("❌") ? 'text-red-700' : 'text-blue-700'
            }`}>
              {message}
            </div>
          </div>
        )}

        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <div className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
            <p><strong>How notifications work:</strong></p>
            <p>• <strong>Browser:</strong> Instant popup notifications on desktop and mobile browsers</p>
            <p>• <strong>Email:</strong> Sent to your account email and any additional emails you configure</p>
            <p>• <strong>Mobile:</strong> Native push notifications via the SiteScope mobile app</p>
          </div>
        </div>
      </Card.Content>
    </Card>
  );
}
